import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import TipModal from 'components/UI/Modal/Tip/TipModal'
import { StoryAction } from 'components-ref/molecules'
import { useModalContext } from 'context/modalContext'
import { likeStory, unlikeStory } from 'services/endpoints/story'
import { useUserProfile } from 'store'
import { type Story } from 'types/api/story'
import { type StoryActionType } from 'types/types'

interface StoryActionProps {
  story: Story
  setIsPaused: (_setIsPaused: boolean) => void
  setAction: (_action: StoryActionType) => void
  openLikesModal: () => void
  openStatisticsModal: () => void
  closeModal: () => void
  openViewsModal: () => void // unused here but kept as-is
}

export function StoryActions({
  story,
  setIsPaused,
  setAction,
  openLikesModal,
  openStatisticsModal,
  closeModal
}: StoryActionProps) {
  const user = useUserProfile()
  const loggedInUserId = user?.id
  const isOwner = Boolean(loggedInUserId && loggedInUserId === story.user_id)

  const modalData = useModalContext()
  const { t } = useTranslation()

  // likes are represented by: story.like_count + story.liked (per your payload)
  const [isLiked, setIsLiked] = useState<boolean>(false)
  const [likeCount, setLikeCount] = useState<number>(0)
  const [isLikeUpdating, setIsLikeUpdating] = useState(false)
  useEffect(() => {
    setIsLiked(Boolean((story as any).liked))
    setLikeCount(Number((story as any).like_count ?? 0))
  }, [story?.id, (story as any)?.liked, (story as any)?.like_count])

  // If modal closes elsewhere, clean up local state
  useEffect(() => {
    if (!modalData.isOpen) {
      closeModal()
    }
  }, [modalData.isOpen, closeModal])

  const likesCountText = useMemo(() => {
    // keep your existing UX: only owner sees the count beside heart
    if (!isOwner) return ''
    return String(likeCount)
  }, [isOwner, likeCount])

  const heartIcon = useMemo(() => {
    // owner heart is a "open likes list" button (not a like toggle)
    if (isOwner) return 'HeartOutline'
    return isLiked ? 'HeartFill' : 'HeartOutline'
  }, [isOwner, isLiked])

  // -----------------------
  // TIP MODAL (RESTORED)
  // -----------------------
  const addTip = ({
    modelId,
    avatarSrc
  }: {
    modelId: string
    avatarSrc: string | null
  }) => {
    setAction?.(null)
    setIsPaused(true)
    modalData.addSecondaryModal(
        t('tipAmount'),
        <TipModal
            tipType="story"
            modelData={{
                storyId: story.id,
                modelId,
                avatarSrc
            }}
            onClose={() => {
                setAction?.('longPress')
                setIsPaused(false)
            }}
        />,
        false,
        false,
        '',
        '',
        true // keyboardAdjust
    )
  }

  function handleTip() {
    addTip({
      modelId: story.user_id,
      avatarSrc: story.user?.avatar?.url ?? null
    })
  }

  // -----------------------
  // HEART CLICK
  // -----------------------
const handleHeartClick = async () => {
  if (!loggedInUserId || isLikeUpdating) return

  if (isOwner) {
    openLikesModal()
    setAction?.('longPress')
    setIsPaused(false)
    return
  }

  const previousLiked = isLiked
  const previousCount = likeCount
  const nextLiked = !isLiked

  setIsLikeUpdating(true)
  setIsLiked(nextLiked)
  setLikeCount(prev => Math.max(0, nextLiked ? prev + 1 : prev - 1))

  try {
    if (nextLiked) {
      await likeStory(story.id)
    } else {
      await unlikeStory(story.id)
    }
  } catch {
    setIsLiked(previousLiked)
    setLikeCount(previousCount)
  } finally {
    setIsLikeUpdating(false)
  }

  setAction?.('longPress')
  setIsPaused(false)
}
  // -----------------------
  // STATS CLICK (OWNER ALWAYS)
  // -----------------------
    const handleStatsClick = () => {
    if (!loggedInUserId) return

    // just open the overlay inside StoryFullscreen
    openStatisticsModal()

    setAction?.('longPress')
    setIsPaused(true)
    }

  return (
    <ActionsContainer>
      <StoryAction icon={heartIcon} text={likesCountText} onClickAction={handleHeartClick} />

      {/* owner can ALWAYS open stats popup (even if no statistics on story payload) */}
      {isOwner && (
        <StoryAction
          icon="PostStats"
          text={`$${Number((story as any).statistics?.tips_sum ?? 0).toFixed(2)}`}
          onClickAction={handleStatsClick}
          location="story"
        />
      )}

      {/* non-owner can tip (restored TipModal behavior) */}
      {!isOwner && <StoryAction icon="LiveCircleTip" text="TIP" onClickAction={handleTip} />}
    </ActionsContainer>
  )
}

const ActionsContainer = styled.div`
  width: 5.6rem;
  position: absolute;
  right: 1.8rem;
  top: 12rem;
  z-index: 99;
  pointer-events: none;
`